/**
 * Custom Models (BYOM) composable for the Model Gateway feature
 * Provides a convenient interface for components to interact with custom models
 * Handles encrypted API key storage and notification handling for CRUD operations
 */

import { computed } from 'vue';
import { useModelGatewayStore } from '@/stores/modelGateway';
import type {
  CustomModelResponse,
  CreateCustomModelRequest,
  UpdateCustomModelRequest,
  CustomModelQueryParams,
} from '../types';

/**
 * Composable for managing custom models (BYOM - Bring Your Own Model)
 * Exposes store state and actions with notification handling
 * Handles encrypted API key storage (API keys are never returned from the server)
 */
export function useCustomModels() {
  const store = useModelGatewayStore();

  /**
   * Fetch custom models with optional filtering
   * @param params - Optional query parameters for filtering
   */
  const fetchCustomModels = async (params?: CustomModelQueryParams): Promise<void> => {
    await store.fetchCustomModels(params);
  };

  /**
   * Create a new custom model
   * Note: API key is encrypted on the server and never returned
   * @param data - The custom model configuration including encrypted API key
   * @returns Promise that resolves to the created model if successful, null otherwise
   */
  const createCustomModel = async (
    data: CreateCustomModelRequest
  ): Promise<CustomModelResponse | null> => {
    return await store.createCustomModel(data);
  };

  /**
   * Update an existing custom model
   * Note: API key is encrypted on the server and never returned
   * @param slug - The unique identifier for the custom model
   * @param data - The updated custom model configuration
   * @returns Promise that resolves to the updated model if successful, null otherwise
   */
  const updateCustomModel = async (
    slug: string,
    data: UpdateCustomModelRequest
  ): Promise<CustomModelResponse | null> => {
    return await store.updateCustomModel(slug, data);
  };

  /**
   * Enable a custom model
   * @param slug - The unique identifier for the custom model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const enableCustomModel = async (slug: string): Promise<boolean> => {
    return await store.enableCustomModel(slug);
  };

  /**
   * Disable a custom model
   * @param slug - The unique identifier for the custom model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const disableCustomModel = async (slug: string): Promise<boolean> => {
    return await store.disableCustomModel(slug);
  };

  /**
   * Delete a custom model
   * @param slug - The unique identifier for the custom model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const deleteCustomModel = async (slug: string): Promise<boolean> => {
    return await store.deleteCustomModel(slug);
  };

  /**
   * Get a custom model by slug
   * @param slug - The unique identifier for the custom model
   * @returns The custom model if found, undefined otherwise
   */
  const getCustomModelBySlug = (slug: string): CustomModelResponse | undefined => {
    return store.getCustomModelBySlug(slug);
  };

  return {
    // State - all custom models from store
    customModels: computed(() => store.customModels),

    // Loading and error state
    isLoading: computed(() => store.isLoadingCustomModels),
    error: computed(() => store.customModelsError),

    // Actions
    fetchCustomModels,
    createCustomModel,
    updateCustomModel,
    enableCustomModel,
    disableCustomModel,
    deleteCustomModel,
    getCustomModelBySlug,
  };
}
